import { Sangha, UserProfile } from "@app/shared/types";
import { darken, alpha, IconButton, lighten, Modal, Slide, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { analyticsTrack } from "app/analytics/track";
import { localImageSrc } from "app/images";
import { LoggedInUserProfileContext } from "features/auth/AuthRoute";
import { TeacherDialog } from "features/community/TeacherDialog";
import { FloatingButton } from "components/FloatingButton";
import { useBreakpointQuery } from "hooks/useBreakPointQuery";
import { useContext, useState } from "react";
import { UserProfileHighlight } from "../profile/UserProfileHighlight";
import { getSanghaMemberProfiles } from "./getSanghaMemberProfiles";
import { MemberDialog } from "./MemberDialog";
import { SanghaSessionPanel } from "./SanghaSessionPanel";
import { theme } from "app/theme";

const getBackgroundStyle = (width?: number) =>
    `linear-gradient(${alpha(theme.palette.grey900, 0.15)}, ${alpha(
        theme.palette.grey900,
        0.15,
    )}), url(${localImageSrc("bg2-min.jpg", width)})`;

const useStyles = makeStyles((theme) => ({
    modal: {},
    title: {
        textAlign: "center",
        marginTop: theme.spacing(10),
        marginBottom: theme.spacing(4),
    },
    subtitle: {
        textAlign: "center",
        marginTop: theme.spacing(4),
    },
    memberShadeContainer: {
        overflowY: "auto",
        outline: 0,
        position: "absolute",
        top: "5%",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: darken(theme.palette.primary500, 0.1),
        backgroundSize: "cover",
        backgroundPosition: "50% 50%",
        color: theme.palette.coral100,
        minWidth: 300,

        backgroundImage: getBackgroundStyle(400),
        [theme.breakpoints.up(400)]: {
            backgroundImage: getBackgroundStyle(600),
        },
        [theme.breakpoints.up(600)]: {
            backgroundImage: getBackgroundStyle(960),
        },
        [theme.breakpoints.up(960)]: {
            backgroundImage: getBackgroundStyle(1280),
        },
        [theme.breakpoints.up(1280)]: {
            backgroundImage: getBackgroundStyle(),
        },
    },
    memberShade: {
        padding: theme.spacing(0, 4),
        paddingBottom: theme.spacing(12),
    },
    closeButton: {
        position: "fixed",
        zIndex: 1,
        left: "50%",
        top: "5%",
        transform: "translateX(-50%) translateY(-40%)",
        color: theme.palette.coral100,
        backgroundColor: theme.palette.primary500,
        border: theme.border.default,
        padding: 0,
        "&:hover": {
            backgroundColor: lighten(theme.palette.primary500, 0.1),
        },
    },

    floatingButton: {
        right: "50%",
        transform: "translateX(50%)",
    },
    sanghaDetails: {
        textAlign: "center",
        padding: theme.spacing(4, 2),
        margin: theme.spacing(4, 0, 4, 0),
        backgroundColor: alpha(theme.palette.primary500, 0.8),

        "& .MuiLink-root": {
            color: theme.palette.coral100,
        },

        "& .MuiTypography-colorTextSecondary": {
            color: alpha(theme.palette.coral100, 0.5),
        },
    },
}));

export const SanghaMembers = (props: {
    sangha: Sangha;
    hasReflectionQuestion: boolean;
    open: boolean;
    onClose: VoidFunction;
    onUpdateMember: VoidFunction;
}) => {
    const [dialogMember, setDialogMember] = useState<UserProfile | null>(null);
    const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false);
    const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);

    const { sangha, open, onClose, onUpdateMember, hasReflectionQuestion } = props;

    const isMobile = useBreakpointQuery("md");

    const classes = useStyles();

    const user = useContext(LoggedInUserProfileContext);

    const profiles = getSanghaMemberProfiles(sangha);

    return (
        <Modal open={open} className={classes.modal} onClose={onClose}>
            <Slide in={open} direction="up">
                <div className={classes.memberShadeContainer} data-testid="sanghaMembersShade">
                    <FloatingButton
                        onClick={onClose}
                        className={classes.floatingButton}
                        variant="primaryInverse"
                    >
                        {isMobile ? "See message board" : "Go to my group"}
                    </FloatingButton>

                    <IconButton
                        classes={{ root: classes.closeButton }}
                        onClick={onClose}
                        data-testid="hideSanghaMembersButton"
                    >
                        <ExpandMoreRoundedIcon />
                    </IconButton>

                    <Typography variant="h2" className={classes.title}>
                        {sangha.name}
                    </Typography>

                    {isMobile ? (
                        <SanghaSessionPanel
                            sangha={sangha}
                            hasReflectionQuestion={hasReflectionQuestion}
                            classes={{ root: classes.sanghaDetails }}
                        />
                    ) : (
                        <Typography variant="h4" className={classes.subtitle}>
                            Your fellow peers
                        </Typography>
                    )}

                    <div className={classes.memberShade}>
                        {dialogMember && (
                            <MemberDialog
                                member={dialogMember}
                                open={dialogMember != null}
                                onClose={() => setDialogMember(null)}
                            />
                        )}

                        <UserProfileHighlight
                            profile={{
                                ...sangha.teacher,
                                nickName: sangha.teacher.name,
                                bio: sangha.teacher.background,
                                isTeacher: true,
                            }}
                            onClick={() => {
                                setTeacherDialogOpen(true);
                                analyticsTrack(`member.sangha.teacher_profile.opened`, {
                                    sanghaId: sangha.id,
                                    teacherName: sangha.teacher.name,
                                });
                            }}
                        />

                        {profiles.map((profile: UserProfile, i: number) => (
                            <UserProfileHighlight
                                key={profile.id}
                                profile={profile}
                                onClick={() => {
                                    setDialogMember(profile);
                                    analyticsTrack(`member.sangha.member_profile.opened`, {
                                        sanghaId: sangha.id,
                                    });
                                }}
                            />
                        ))}

                        <TeacherDialog
                            open={teacherDialogOpen}
                            onClose={() => setTeacherDialogOpen(false)}
                            teacher={sangha.teacher}
                        />
                    </div>
                </div>
            </Slide>
        </Modal>
    );
};
